import React from "react";
import { Link } from "react-router-dom";
import SminHeader from "@/components/SminHeader";
import SminFooter from "@/components/SminFooter";
import { MARKETING_NAV as NAV } from "@/lib/marketingNav";
import {
  Mail, Phone, MapPin, Target, Users, Award, ArrowRight, MessageCircle,
  Zap, Heart, ShieldCheck, Compass, Globe,
} from "lucide-react";

const VALUES = [
  { Icon: Target,     title: "Purpose-Built",     body: "Designed exclusively for exploration, resource and mine-planning geologists — not repurposed generic GIS." },
  { Icon: Users,      title: "Practitioner-First", body: "Every module is shaped by working competent persons — from code-compliant reporting wording to LG bench-slope defaults." },
  { Icon: Award,      title: "Enterprise-Grade",  body: "SOC-2-track security, role-based access, signed PDF audit trails, and on-prem options for producers." },
  { Icon: Heart,      title: "Customer-Obsessed", body: "Every feature request from a paying customer is triaged inside 48 hours. Our roadmap is public." },
];

const TIMELINE = [
  { year: "2022", title: "The problem",   body: "Founders finished a code-compliant gold resource project after 4 months of forklift-CSV loops between Datamine, Excel and Word. Decided there must be a better way." },
  { year: "2023", title: "SMIN v0",       body: "First internal build. Ingested 12 000 m of RC drilling, kriged a gold block model and generated a signed PDF report — all in a browser." },
  { year: "2024", title: "First customers", body: "10 paying customers across Australia, Africa and India. Team of 12. First on-prem deployment." },
  { year: "2025", title: "Portfolio scale", body: "Mid-tier copper producer with 4 operating assets migrates from four different desktop stacks. 25M+ blocks on one workspace." },
  { year: "2026", title: "Where we are",  body: "Team of 25+, three continents, 60+ customers spanning junior explorers, consultancies, mid-tier producers and government surveys." },
];

const TRUST_BADGES = [
  { name: "CRIRSCO",         body: "Aligned with the CRIRSCO international reporting template — the umbrella every national code inherits from." },
  { name: "SAMREC · PERC",   body: "Full Table 1 disclosures for JSE (South Africa) and LSE / AIM / Euronext (Europe) listings." },
  { name: "SME · S-K 1300",  body: "Society for Mining Guide + US SEC Regulation S-K subpart 1300 property disclosure." },
  { name: "KAZRC · NAEN",    body: "Kazakhstan and Russian public reporting standards for exchange listings across the CIS region." },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen flex flex-col bg-white text-neutral-900" data-testid="page-about">
      <SminHeader variant="marketing" forceGuest navLinks={NAV} />

      <main className="flex-1">
        {/* Hero */}
        <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 pb-8 text-center">
          <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#4CAF50" }}>
            About
          </div>
          <h1 className="mt-3 text-3xl md:text-5xl font-bold tracking-tight max-w-3xl mx-auto leading-tight">
            Modern mine planning, made by miners.
          </h1>
          <p className="mt-5 max-w-2xl mx-auto text-neutral-600 text-sm md:text-base leading-relaxed">
            SMIN — Shanvi Mining Intelligence — is the flagship product of{" "}
            <strong className="text-neutral-900">Shanvi Resources</strong>, a mining-tech company on a mission
            to move the industry off legacy desktop stacks and onto a modern, collaborative, browser-based platform.
          </p>
        </section>

        {/* At-a-glance stats */}
        <section className="bg-neutral-50 py-10 border-y border-neutral-200">
          <div className="max-w-6xl mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {[["2023", "Founded"], ["60+", "Customers"], ["25+", "Team"], ["3", "Continents"]].map(([n, l]) => (
              <div key={l}>
                <div className="text-2xl md:text-3xl font-bold tabular-nums" style={{ color: "#4CAF50" }}>{n}</div>
                <div className="mt-1 text-[11px] uppercase tracking-widest text-neutral-500">{l}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Values */}
        <section className="max-w-7xl mx-auto px-6 md:px-10 py-16">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#4CAF50" }}>
              What we value
            </div>
            <h2 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight">
              Four principles that shape every decision.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map(({ Icon, title, body }) => (
              <div key={title} className="p-6 rounded-xl border border-neutral-200 hover:border-[#4CAF50] transition bg-white">
                <span className="inline-flex items-center justify-center h-11 w-11 rounded-lg" style={{ background: "#4CAF5015" }}>
                  <Icon className="h-5 w-5" style={{ color: "#4CAF50" }} strokeWidth={2} />
                </span>
                <h3 className="mt-4 text-base font-bold">{title}</h3>
                <p className="mt-1.5 text-sm text-neutral-600 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Story timeline */}
        <section className="bg-neutral-50 py-16">
          <div className="max-w-4xl mx-auto px-6 md:px-10">
            <div className="text-center mb-10">
              <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#4CAF50" }}>
                Our story
              </div>
              <h2 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight">
                From a JORC-code gold project to a global platform.
              </h2>
            </div>
            <ol className="relative border-l border-neutral-200 ml-4 md:ml-6 space-y-8">
              {TIMELINE.map((t) => (
                <li key={t.year} className="relative pl-6 md:pl-10">
                  <span
                    className="absolute -left-[17px] md:-left-[21px] flex items-center justify-center h-9 w-9 md:h-11 md:w-11 rounded-full ring-4 ring-neutral-50 text-white text-xs font-bold tabular-nums"
                    style={{ background: "#4CAF50" }}
                  >
                    {t.year.slice(-2)}
                  </span>
                  <div className="pb-1">
                    <div className="text-xs font-bold uppercase tracking-widest text-neutral-500">{t.year}</div>
                    <h3 className="mt-1 text-base font-bold">{t.title}</h3>
                    <p className="mt-1.5 text-sm text-neutral-600 leading-relaxed">{t.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Trust / compliance */}
        <section className="bg-neutral-50 py-16">
          <div className="max-w-6xl mx-auto px-6 md:px-10">
            <div className="text-center max-w-2xl mx-auto mb-10">
              <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#4CAF50" }}>
                Trusted globally
              </div>
              <h2 className="mt-2 text-2xl md:text-3xl font-bold tracking-tight">
                Aligned with the codes your regulators require.
              </h2>
            </div>
            <div className="grid md:grid-cols-4 gap-4">
              {TRUST_BADGES.map((b) => (
                <div key={b.name} className="p-6 rounded-xl border border-neutral-200 bg-white">
                  <div className="flex items-start gap-3">
                    <ShieldCheck className="h-5 w-5 mt-0.5 shrink-0" style={{ color: "#4CAF50" }} />
                    <div>
                      <div className="text-sm font-bold text-neutral-900">{b.name}</div>
                      <div className="text-xs text-neutral-600 mt-1 leading-relaxed">{b.body}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact block */}
        <section className="max-w-7xl mx-auto px-6 md:px-10 py-16">
          <div
            className="rounded-2xl px-8 md:px-12 py-10 text-white"
            style={{ background: "linear-gradient(135deg, #4CAF50 0%, #3d9142 100%)" }}
          >
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold tracking-tight">Get in touch.</h2>
                <p className="mt-3 text-sm text-white/90 max-w-md">
                  Book a 30-minute demo, request a quote, or just say hi.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/contact"
                    data-testid="about-contact-cta"
                    className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-white font-semibold text-sm"
                    style={{ color: "#4CAF50" }}
                  >
                    Contact page <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
              <ul className="space-y-4 text-sm">
                <li className="flex items-center gap-3">
                  <Mail className="h-4 w-4" />
                  <a href="mailto:info@sresources.co" className="hover:underline">info@sresources.co</a>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="h-4 w-4" />
                  <a href="tel:+918079989999" className="hover:underline">+91 80799 89999</a>
                </li>
                <li className="flex items-center gap-3">
                  <MessageCircle className="h-4 w-4" />
                  <a href="https://wa.me/919938872207" target="_blank" rel="noreferrer" className="hover:underline">
                    WhatsApp +91 99388 72207
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <MapPin className="h-4 w-4" />
                  <span>Bengaluru, Karnataka, India</span>
                </li>
              </ul>
            </div>
          </div>
        </section>
      </main>

      <SminFooter />
    </div>
  );
}
